import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { toast } from "sonner";
import { Plus, User, Users, Upload, Cloud, Loader2, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "../contexts";
import { AppointmentModal, AppointmentDetailModal, BulkImportModal } from "../components/modals";
import MainLayout from "../components/MainLayout";
import { CustomEvent, CustomAgendaEvent, TeamViewCalendar } from "../components/calendar";
import { API } from '../config/api';

const localizer = momentLocalizer(moment);

const CalendarPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const user = auth?.user;
  const [appointments, setAppointments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [teamInstallers, setTeamInstallers] = useState([]);
  const [dailyOverrides, setDailyOverrides] = useState({});
  const [userNames, setUserNames] = useState({});
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showUnscheduledList, setShowUnscheduledList] = useState(false);

  // Restore calendar state from sessionStorage
  const dateParam = searchParams.get('date');
  const saved = sessionStorage.getItem('calendarState');
  const restored = saved ? JSON.parse(saved) : {};

  const [selectedTeam, setSelectedTeam] = useState(restored.selectedTeam || "");
  const [currentDate, setCurrentDate] = useState(
    dateParam ? new Date(dateParam + 'T12:00:00') : (restored.currentDate ? new Date(restored.currentDate) : new Date())
  );
  const [currentView, setCurrentView] = useState(dateParam ? "day" : "outlook");
  // Default: installer parte da Vista Outlook in modalita "giorno"
  const initialViewMode = user?.role === 'installer' ? 'team' : 'team';
  const isRestrictedInstaller = user?.role === 'installer' && !user?.calendar_view_all;
  const initialOutlookMode = isRestrictedInstaller ? 'day' : (restored.outlookMode || 'week');
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [outlookMode, setOutlookMode] = useState(initialOutlookMode);
  const [selectedInstallerIds, setSelectedInstallerIds] = useState(restored.selectedInstallerIds || []);

  // Persist calendar state on changes (only safe values)
  useEffect(() => {
    sessionStorage.setItem('calendarState', JSON.stringify({
      currentDate: currentDate.toISOString(),
      outlookMode,
      selectedTeam,
      selectedInstallerIds,
    }));
  }, [currentDate, outlookMode, selectedTeam, selectedInstallerIds]);

  const triggerSfSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/salesforce/sync-now`);
      if (res.data?.sites_updated > 0) {
        toast.success(res.data.message, { duration: 6000 });
      } else {
        toast.success('Sync Salesforce completata');
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedTeam]);

  useEffect(() => {
    if (viewMode === "team") {
      // Installer-only: in Vista Outlook deve vedere SOLO se stesso (regola di privacy)
      // Eccezione: installatore con visibilita' estesa (calendar_view_all) vede tutti
      if (isRestrictedInstaller && user?.id) {
        setTeamInstallers([{
          id: user.id,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          email: user.email,
        }]);
        return;
      }
      // selectedTeam === "" significa "Tutti i team" -> carica installers di tutti i team visibili
      fetchTeamInstallers(selectedTeam || "ALL");
    }
  }, [viewMode, selectedTeam, teams, user]);

  const fetchData = async () => {
    try {
      let aptsUrl = `${API}/appointments`;
      const params = [];
      if (selectedTeam) params.push(`team_id=${selectedTeam}`);
      // Installers only see their own appointments (salvo visibilita' estesa)
      if (isRestrictedInstaller && user?.id) params.push(`installer_id=${user.id}`);
      // Default date window around currentDate to avoid hitting the server-side cap
      // when a workspace has grown beyond ~5-10k total appointments.
      // Window: 60 giorni indietro -> 15 mesi avanti (abbondante per Vista Outlook/Mese/Timeline).
      try {
        const base = currentDate instanceof Date ? currentDate : new Date();
        const start = new Date(base); start.setDate(start.getDate() - 60); start.setHours(0, 0, 0, 0);
        const end = new Date(base); end.setMonth(end.getMonth() + 15); end.setHours(23, 59, 59, 999);
        params.push(`start_date=${start.toISOString()}`);
        params.push(`end_date=${end.toISOString()}`);
      } catch (e) { /* ignore date math failures */ }
      if (params.length) aptsUrl += `?${params.join('&')}`;
      
      const [aptsRes, teamsRes] = await Promise.all([
        axios.get(aptsUrl),
        axios.get(`${API}/teams?visible_only=true`),
      ]);
      setAppointments(aptsRes.data);
      setTeams(teamsRes.data);
      
      // Fetch creator names and installer names for appointments
      const creatorIds = [...new Set(aptsRes.data.map(a => a.created_by).filter(Boolean))];
      const installerIds = [...new Set(aptsRes.data.map(a => a.assigned_installer_id).filter(Boolean))];
      const allUserIds = [...new Set([...creatorIds, ...installerIds])];
      
      if (allUserIds.length > 0) {
        try {
          const namesRes = await axios.post(`${API}/users/names`, allUserIds);
          setUserNames(prev => ({ ...prev, ...namesRes.data }));
        } catch (e) {
          console.error("Error fetching user names:", e);
        }
      }
    } catch (e) {
      toast.error(t('appointment.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamInstallers = async (teamId) => {
    try {
      // "ALL" -> aggregate installers from every visible team
      if (teamId === "ALL") {
        const visibleTeams = teams.filter(t => t.is_visible !== false);
        if (visibleTeams.length === 0) {
          setTeamInstallers([]);
          return;
        }
        const results = await Promise.all(
          visibleTeams.map(t => axios.get(`${API}/teams/${t.id}/installers`).catch(() => ({ data: [] })))
        );
        const merged = [];
        const seen = new Set();
        results.forEach(r => {
          (r.data || []).forEach(inst => {
            if (!seen.has(inst.id)) {
              seen.add(inst.id);
              merged.push(inst);
            }
          });
        });
        setTeamInstallers(merged);
        return;
      }
      const res = await axios.get(`${API}/teams/${teamId}/installers`);
      setTeamInstallers(res.data);
    } catch (e) {
      console.error("Error fetching team installers:", e);
      setTeamInstallers([]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "#22c55e";
      case "pending_validation": return "#f59e0b";
      case "not_completed": return "#ef4444";
      case "to_reschedule": return "#f59e0b";
      default: return "#3b82f6";
    }
  };

  // Colors by intervention type
  const getInterventionColor = (interventionType) => {
    switch (interventionType) {
      case "installation": return "#3B82F6";    // Blu
      case "cq": return "#22C55E";              // Verde
      case "cq_pf": return "#F97316";           // Arancione
      case "ca": return "#EAB308";              // Giallo
      case "training": return "#A855F7";        // Viola
      case "uninstall": return "#6B7280";       // Grigio
      default: return "#3B82F6";                // Default Blu
    }
  };

  const visibleAppointments = appointments;

  const events = visibleAppointments.map((apt) => {
    const timeRange = apt.scheduled_time 
      ? `${apt.scheduled_time}${apt.scheduled_end_time ? '-' + apt.scheduled_end_time : ''} ` 
      : '';
    return {
      id: apt.id,
      title: `${timeRange}${apt.client_name}${apt.license_plate ? ' - ' + apt.license_plate : ''}`,
      start: new Date(apt.scheduled_date),
      end: new Date(apt.scheduled_date),
      allDay: true,
      resource: apt,
      color: getInterventionColor(apt.intervention_type),
      statusColor: getStatusColor(apt.status),
      status: apt.status,
      createdByName: userNames[apt.created_by] || null,
      installerName: apt.assigned_installer_id ? userNames[apt.assigned_installer_id] : null,
    };
  });

  const eventStyleGetter = (event) => {
    const isCompleted = event.status === "completed";
    const isPendingValidation = event.status === "pending_validation";
    const isNotCompleted = event.status === "not_completed";
    const isToReschedule = event.status === "to_reschedule";
    const isConfirmed = event.status === "confirmed";
    const isProvisional = event.status === "provisional";
    
    return {
      style: {
        backgroundColor: isCompleted ? "#166534"
          : isPendingValidation ? "#b45309"
          : isNotCompleted ? "#991b1b"
          : isProvisional ? "#7e22ce"
          : isConfirmed ? "#2563eb"
          : event.color,
        borderRadius: "6px",
        opacity: 1,
        color: "white",
        border: isCompleted 
          ? "3px solid #22c55e"
          : isPendingValidation
            ? "3px dashed #f59e0b"
            : isNotCompleted 
              ? "3px solid #ef4444"
              : isToReschedule
                ? "3px solid #f59e0b"
                : isConfirmed
                  ? "3px solid #60a5fa"
                  : isProvisional
                    ? "3px dashed #c084fc"
                    : "none",
        display: "block",
        textDecoration: "none",
        fontWeight: isCompleted || isConfirmed || isProvisional ? "600" : "normal",
      },
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedAppointment(event.resource);
    setShowStatusModal(true);
  };

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowStatusModal(true);
  };

  // Sync selectedAppointment con la lista appointments dopo refresh silenti
  // (es. chiusura per-veicolo). Senza questo, il modal restava con dati stale.
  useEffect(() => {
    if (!selectedAppointment || !showStatusModal) return;
    const refreshed = appointments.find(a => a.id === selectedAppointment.id);
    if (refreshed && refreshed !== selectedAppointment) {
      setSelectedAppointment(refreshed);
    }
  }, [appointments, selectedAppointment, showStatusModal]);

  const canCreateAppointment = user?.role === "superadmin" || user?.role === "admin" || user?.role === "operator";
  const canViewTeamView = user?.role === "superadmin" || user?.role === "admin" || user?.role === "operator" || user?.role === "quality_control" || user?.role === "installer";
  const isQualityControl = user?.role === "quality_control";
  
  // Calendar messages based on current language
  const calendarMessages = {
    today: t('calendar.messages.today'),
    previous: t('calendar.messages.previous'),
    next: t('calendar.messages.next'),
    month: t('calendar.messages.month'),
    week: t('calendar.messages.week'),
    day: t('calendar.messages.day'),
    agenda: t('calendar.messages.agenda'),
    date: t('calendar.messages.date'),
    time: t('calendar.messages.time'),
    event: t('calendar.messages.event'),
    noEventsInRange: t('calendar.messages.noEvents'),
  };

  return (
    <MainLayout>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800" data-testid="calendar-title">{t('calendar.title')}</h1>
          <p className="text-gray-600 mt-1">
            {user?.role === "installer" ? t('calendar.yourCalendar') : t('calendar.manageAppointments')}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* View Mode Toggle - hidden for installers (they see only Outlook view) */}
          {canViewTeamView && user?.role !== "installer" && (
            <div className="flex bg-gray-100 rounded-lg p-1.5 gap-1">
              <button
                onClick={() => { setViewMode("team"); setCurrentView("outlook"); }}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === "team" 
                    ? "bg-lojack-red text-white shadow-md hover:bg-red-700" 
                    : "bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-400 hover:shadow-sm"
                }`}
                data-testid="view-mode-team"
              >
                {t('calendar.teamView')}
              </button>
              <button
                onClick={() => { setViewMode("standard"); setCurrentView("month"); }}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  viewMode === "standard" 
                    ? "bg-lojack-red text-white shadow-md hover:bg-red-700" 
                    : "bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-400 hover:shadow-sm"
                }`}
                data-testid="view-mode-standard"
              >
                {t('calendar.standardView')}
              </button>
            </div>
          )}

          {user?.role !== "installer" && (
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red focus:border-transparent"
              data-testid="team-filter"
            >
              <option value="">{t('calendar.allTeams')}</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          )}

          {(user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'operator' || user?.role === 'quality_control') && (
            <button
              onClick={triggerSfSync}
              disabled={syncing}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              data-testid="calendar-sf-sync-btn"
              title="Sincronizza stati da Salesforce"
            >
              {syncing ? <Loader2 size={18} className="animate-spin" /> : <Cloud size={18} />}
              <span className="hidden sm:inline">{syncing ? 'Sync...' : 'Sync SF'}</span>
            </button>
          )}

          {canCreateAppointment && (
            <>
              <button
                onClick={() => setShowBulkImportModal(true)}
                className="flex items-center gap-2 bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition"
                data-testid="bulk-import-btn"
                title="Importa appuntamenti da Excel"
              >
                <Upload size={20} />
                <span className="hidden sm:inline">Import Excel</span>
              </button>
              <button
                onClick={() => {
                  setSelectedAppointment(null);
                  setShowModal(true);
                }}
                className="flex items-center gap-2 bg-lojack-red text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                data-testid="new-appointment-btn"
              >
                <Plus size={20} />
                {t('calendar.newAppointment')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Banner: Appuntamenti senza orario (00:00 o vuoto) - sarebbero invisibili sul calendario */}
      {(() => {
        const unscheduled = appointments.filter(a =>
          (!a.scheduled_time || a.scheduled_time === '00:00') &&
          a.status !== 'not_completed' &&
          a.intervention_type !== 'absence'
        );
        if (unscheduled.length === 0) return null;
        return (
          <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="unscheduled-banner">
            <button
              type="button"
              onClick={() => setShowUnscheduledList(v => !v)}
              className="w-full flex items-center justify-between gap-2 text-left"
              data-testid="unscheduled-banner-toggle"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">
                  {unscheduled.length} appuntament{unscheduled.length === 1 ? 'o' : 'i'} senza orario
                </span>
                <span className="text-xs text-amber-700">— non visibil{unscheduled.length === 1 ? 'e' : 'i'} sul calendario, clicca per aprirli e assegnare un orario</span>
              </div>
              {showUnscheduledList ? <ChevronUp size={16} className="text-amber-700" /> : <ChevronDown size={16} className="text-amber-700" />}
            </button>
            {showUnscheduledList && (
              <ul className="mt-3 space-y-1 border-t border-amber-200 pt-2" data-testid="unscheduled-list">
                {unscheduled.slice(0, 30).map(apt => (
                  <li key={apt.id}>
                    <button
                      type="button"
                      onClick={() => handleAppointmentClick(apt)}
                      className="w-full text-left px-2 py-1 text-sm text-amber-900 hover:bg-amber-100 rounded transition"
                      data-testid={`unscheduled-item-${apt.id}`}
                    >
                      <span className="font-medium">{new Date(apt.scheduled_date).toLocaleDateString('it-IT')}</span>
                      {' · '}
                      <span>{apt.client_name || '(senza cliente)'}</span>
                      {apt.city && <span className="text-amber-700"> · {apt.city}</span>}
                      {apt.assigned_installer_id && userNames[apt.assigned_installer_id] && (
                        <span className="text-amber-700"> · {userNames[apt.assigned_installer_id]}</span>
                      )}
                    </button>
                  </li>
                ))}
                {unscheduled.length > 30 && (
                  <li className="text-xs text-amber-700 pl-2">…e altri {unscheduled.length - 30}</li>
                )}
              </ul>
            )}
          </div>
        );
      })()}

      {/* Legend - Intervention Types */}
      <div className="flex flex-wrap gap-4 mb-4 bg-white p-4 rounded-lg shadow-sm">
        <span className="text-sm font-medium text-gray-700 mr-2">Tipo Intervento:</span>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#3B82F6" }}></div>
          <span className="text-sm text-gray-600">Installazione</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#22C55E" }}></div>
          <span className="text-sm text-gray-600">CQ</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#F97316" }}></div>
          <span className="text-sm text-gray-600">CQ PF</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#EAB308" }}></div>
          <span className="text-sm text-gray-600">CA</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#A855F7" }}></div>
          <span className="text-sm text-gray-600">Formazione</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#6B7280" }}></div>
          <span className="text-sm text-gray-600">Disinstallazione</span>
        </div>
      </div>

      {/* Legenda Stati */}
      <div className="flex flex-wrap gap-4 mb-4 bg-white p-4 rounded-lg shadow-sm">
        <span className="text-sm font-medium text-gray-700 mr-2">Stati:</span>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#2563eb" }}></div>
          <span className="text-sm text-gray-600">Confermato</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-dashed" style={{ backgroundColor: "#7e22ce", borderColor: "#c084fc" }}></div>
          <span className="text-sm text-gray-600">Provvisorio</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#166534" }}></div>
          <span className="text-sm text-gray-600">Eseguito</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-dashed" style={{ backgroundColor: "#b45309", borderColor: "#f59e0b" }}></div>
          <span className="text-sm text-gray-600">In Attesa Validazione SF</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#991b1b" }}></div>
          <span className="text-sm text-gray-600">Non Eseguito</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2" style={{ borderColor: "#f59e0b" }}></div>
          <span className="text-sm text-gray-600">Da Rischedulare</span>
        </div>
      </div>

      {/* Filtro "Solo job SF pending" RIMOSSO su richiesta Veronica 2026-07-09:
          il calendario mostra sempre la panoramica completa */}

      {/* Calendar View - Standard or Team */}
      {viewMode === "standard" ? (
        <div className="bg-white rounded-xl shadow-sm p-4 lg:p-6" data-testid="calendar-container">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lojack-red"></div>
            </div>
          ) : (
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleSelectEvent}
              date={currentDate}
              onNavigate={(date) => setCurrentDate(date)}
              view={currentView}
              onView={(view) => setCurrentView(view)}
              culture={i18n.language}
              messages={calendarMessages}
              components={{
                event: CustomEvent,
                agenda: {
                  event: CustomAgendaEvent,
                },
              }}
            />
          )}
        </div>
      ) : (
        <TeamViewCalendar
          selectedTeam={selectedTeam}
          teamInstallers={teamInstallers}
          appointments={visibleAppointments}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          currentView={currentView}
          setCurrentView={setCurrentView}
          outlookMode={outlookMode}
          setOutlookMode={setOutlookMode}
          selectedInstallerIds={selectedInstallerIds}
          setSelectedInstallerIds={setSelectedInstallerIds}
          onAppointmentClick={handleAppointmentClick}
          dailyOverrides={dailyOverrides}
          setDailyOverrides={setDailyOverrides}
        />
      )}

      {/* Appointment Modal */}
      {showModal && (
        <AppointmentModal
          appointment={selectedAppointment}
          teams={teams}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            fetchData();
          }}
        />
      )}

      {/* Status Modal */}
      {showStatusModal && selectedAppointment && (
        <AppointmentDetailModal
          appointment={selectedAppointment}
          teams={teams}
          allAppointments={appointments}
          userNames={userNames}
          onClose={() => setShowStatusModal(false)}
          onUpdate={() => {
            setShowStatusModal(false);
            fetchData();
          }}
          onDocumentUpdate={() => {
            fetchData();
          }}
          onEdit={() => {
            setShowStatusModal(false);
            setShowModal(true);
          }}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <BulkImportModal
          teams={teams}
          onClose={() => setShowBulkImportModal(false)}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}
    </MainLayout>
  );
};

export default CalendarPage;
